import { useEffect, useState, useCallback } from "react";
import { CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../../utils/authStorage";
import { orderAPI } from "../../utils/api";
import { toast } from "react-toastify";

const PaymentCallback = () => {
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState({
    status: "checking",
    message: "Verifying payment...",
    transactionId: null,
  });

  /**
   * Debug helper to check order status on backend
   */
  const debugPayment = useCallback(async (id) => {
    try {
      const token = getAccessToken();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/orders/debug-payment/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const debug = await response.json();
        console.log("🎯 Payment Debug Info:", debug);
      }
    } catch (error) {
      console.warn("Debug check failed:", error);
    }
  }, []);

  /**
   * Handle Razorpay signature verification
   */
  const handleRazorpayVerification = useCallback(
    async (paymentData) => {
      try {
        setPaymentStatus({
          status: "checking",
          message: "Verifying your Razorpay payment...",
          transactionId: paymentData.razorpay_payment_id,
        });

        const response = await orderAPI.verifyRazorpayPayment(paymentData);

        if (response.success) {
          toast.success("Payment Successful! Your order is being processed.");
          setPaymentStatus({
            status: "success",
            message: "Payment verified successfully!",
            transactionId: paymentData.razorpay_payment_id,
          });
          // Redirect to orders page after a short delay
          setTimeout(() => navigate("/user-dashboard/orders"), 3000);
        } else {
          throw new Error(response.message || "Verification failed");
        }
      } catch (error) {
        console.error("Razorpay verification error:", error);
        const errorMsg = error.message || "Payment verification failed.";
        toast.error(errorMsg);
        setPaymentStatus({
          status: "failed",
          message: `${errorMsg} Please contact support if the amount was deducted.`,
          transactionId: paymentData.razorpay_payment_id,
        });
      }
    },
    [navigate],
  );

  /**
   * Polling check for other payment methods (like MatrixSols/PhonePe)
   */
  const checkPaymentStatus = useCallback(
    async (idToCheck, originalTransactionId, currentRetryCount = 0) => {
      try {
        const token = getAccessToken();

        // 1. Direct order status check
        const directOrderResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/orders/check-status/${idToCheck}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (directOrderResponse.ok) {
          const orderData = await directOrderResponse.json();
          if (orderData.success && orderData.order) {
            const order = orderData.order;

            if (["completed", "processing", "paid"].includes(order.status)) {
              setPaymentStatus({
                status: "success",
                message: "Order processed successfully!",
                transactionId: idToCheck,
              });
              setTimeout(() => navigate("/user-dashboard/orders"), 3000);
              return;
            } else if (order.status === "failed") {
              setPaymentStatus({
                status: "failed",
                message: "Order payment failed or was rejected.",
                transactionId: idToCheck,
              });
              return;
            }
          }
        }

        // 2. Retry logic (up to 8 times, every 3 seconds)
        if (currentRetryCount < 8) {
          setPaymentStatus({
            status: "pending",
            message: `Verifying payment... (Attempt ${currentRetryCount + 1}/8)`,
            transactionId: idToCheck,
          });
          setTimeout(
            () =>
              checkPaymentStatus(
                idToCheck,
                originalTransactionId,
                currentRetryCount + 1,
              ),
            3000,
          );
        } else {
          setPaymentStatus({
            status: "error",
            message:
              "Payment verification is taking longer than expected. Please check your order history in a few minutes.",
            transactionId: idToCheck,
          });
        }
      } catch (error) {
        console.error("Status check error:", error);
        setPaymentStatus({
          status: "error",
          message: "Unable to verify payment status automatically.",
          transactionId: idToCheck,
        });
      }
    },
    [navigate],
  );

  useEffect(() => {
    // Combine parameters from both search (?) and hash (#)
    // Sometimes Razorpay or redirects put params after the hash in SPAs
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      window.location.hash.includes("?")
        ? window.location.hash.split("?")[1]
        : window.location.hash.replace("#", ""),
    );

    const getParam = (name) => searchParams.get(name) || hashParams.get(name);

    // Standard params
    const transactionId = getParam("transactionId");
    const merchantOrderId = getParam("merchantOrderId");

    // Razorpay params
    const razorpay_payment_id = getParam("razorpay_payment_id");
    // Handle both standard orders and Payment Link redirects
    const razorpay_order_id =
      getParam("razorpay_order_id") || getParam("razorpay_payment_link_id");
    const razorpay_signature = getParam("razorpay_signature");
    const razorpay_payment_link_reference_id = getParam(
      "razorpay_payment_link_reference_id",
    );
    const razorpay_payment_link_status = getParam(
      "razorpay_payment_link_status",
    );

    console.log("=== PAYMENT CALLBACK INITIATED ===", {
      razorpay_payment_id,
      razorpay_order_id,
      razorpay_signature,
      razorpay_payment_link_reference_id,
      razorpay_payment_link_status,
      search: Object.fromEntries(searchParams.entries()),
      hash: Object.fromEntries(hashParams.entries()),
    });

    // Case 1: Razorpay Redirection
    // Ensure all three mandatory parameters are present before calling verification
    if (razorpay_payment_id && razorpay_signature && razorpay_order_id) {
      handleRazorpayVerification({
        razorpay_payment_id,
        razorpay_order_id,
        razorpay_signature,
        razorpay_payment_link_reference_id,
        razorpay_payment_link_status,
      });
      return;
    }

    // Case 2: Other payment methods (transactionId based)
    const idToCheck = transactionId || merchantOrderId;
    if (idToCheck) {
      debugPayment(idToCheck);
      checkPaymentStatus(idToCheck, transactionId);
      return;
    }

    // Case 3: Missing parameters
    setPaymentStatus({
      status: "error",
      message: "Invalid callback: No transaction details found in URL.",
    });
  }, [handleRazorpayVerification, checkPaymentStatus, debugPayment]);

  const getStatusContent = () => {
    switch (paymentStatus.status) {
      case "success":
        return {
          icon: <CheckCircle className="w-16 h-16 text-green-500" />,
          title: "Payment Successful!",
          color: "text-green-600",
        };
      case "failed":
        return {
          icon: <XCircle className="w-16 h-16 text-red-500" />,
          title: "Payment Failed",
          color: "text-red-600",
        };
      case "pending":
      case "checking":
        return {
          icon: <Clock className="w-16 h-16 text-yellow-500 animate-pulse" />,
          title:
            paymentStatus.status === "checking"
              ? "Verifying..."
              : "Processing...",
          color: "text-yellow-600",
        };
      default:
        return {
          icon: <AlertCircle className="w-16 h-16 text-blue-500" />,
          title: "Status Update",
          color: "text-blue-600",
        };
    }
  };

  const content = getStatusContent();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="flex justify-center mb-6">{content.icon}</div>

        <h2 className={`text-2xl font-bold mb-4 ${content.color}`}>
          {content.title}
        </h2>

        <p className="text-gray-600 mb-6">{paymentStatus.message}</p>

        {paymentStatus.transactionId && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              Transaction Ref
            </p>
            <p className="font-mono text-sm break-all">
              {paymentStatus.transactionId}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {paymentStatus.status === "success" && (
            <p className="text-sm text-gray-400">
              Redirecting to your orders...
            </p>
          )}

          {["failed", "error"].includes(paymentStatus.status) && (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate("/user-dashboard/orders")}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View My Orders
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="flex-1 bg-gray-100 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Retry Check
              </button>
            </div>
          )}

          {paymentStatus.status === "pending" && (
            <button
              type="button"
              onClick={() => navigate("/user-dashboard/orders")}
              className="bg-gray-100 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Go to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
